import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getHomepageContent } from "@/lib/cms/homepage.functions";
import {
  HOMEPAGE_FALLBACKS,
  type HomepageContent,
} from "@/lib/homepage-defaults";
import { isValidCmsLink } from "@/lib/cms/link";

/**
 * Merges the CMS homepage row with hardcoded fallbacks per field. Any NULL,
 * empty, or (for link fields) invalid value falls back to the hardcoded
 * default so the rendered homepage always looks correct.
 */
export function useHomepageContent(): HomepageContent {
  const fetchFn = useServerFn(getHomepageContent);
  const { data, isFetched } = useQuery({
    queryKey: ["homepage-content"],
    queryFn: () => fetchFn(),
    staleTime: 60_000,
  });

  const pick = (value: string | null | undefined, fallback: string): string =>
    typeof value === "string" && value.length > 0 ? value : fallback;

  const pickLink = (value: string | null | undefined, fallback: string): string =>
    typeof value === "string" && value.length > 0 && isValidCmsLink(value)
      ? value
      : fallback;

  const pickImage = (value: string | null | undefined, fallback: string): string => {
    if (typeof value !== "string" || value.length === 0) return fallback;
    if (value.startsWith("/") && !value.startsWith("//")) return value;
    try {
      if (new URL(value).protocol === "https:") return value;
    } catch {
      /* fall through */
    }
    return fallback;
  };

  return {
    promotion: data?.promotion ?? null,
    liveAnnouncementEnabled: data?.liveAnnouncementEnabled === true,
    heroBadge: pick(data?.heroBadge, HOMEPAGE_FALLBACKS.heroBadge),
    heroTitleLine1: pick(data?.heroTitleLine1, HOMEPAGE_FALLBACKS.heroTitleLine1),
    heroTitleAccent: pick(data?.heroTitleAccent, HOMEPAGE_FALLBACKS.heroTitleAccent),
    heroTitleLine2: pick(data?.heroTitleLine2, HOMEPAGE_FALLBACKS.heroTitleLine2),
    heroSubtitle: pick(data?.heroSubtitle, HOMEPAGE_FALLBACKS.heroSubtitle),
    primaryButtonText: pick(data?.primaryButtonText, HOMEPAGE_FALLBACKS.primaryButtonText),
    primaryButtonLink: pickLink(data?.primaryButtonLink, HOMEPAGE_FALLBACKS.primaryButtonLink),
    secondaryButtonText: pick(data?.secondaryButtonText, HOMEPAGE_FALLBACKS.secondaryButtonText),
    secondaryButtonLink: pickLink(data?.secondaryButtonLink, HOMEPAGE_FALLBACKS.secondaryButtonLink),
    whyChooseHeading: pick(data?.whyChooseHeading, HOMEPAGE_FALLBACKS.whyChooseHeading),
    whyChooseDescription: pick(data?.whyChooseDescription, HOMEPAGE_FALLBACKS.whyChooseDescription),
    founderHeading: pick(data?.founderHeading, HOMEPAGE_FALLBACKS.founderHeading),
    founderDescription: pick(data?.founderDescription, HOMEPAGE_FALLBACKS.founderDescription),
    mealCategoriesHeading: pick(data?.mealCategoriesHeading, HOMEPAGE_FALLBACKS.mealCategoriesHeading),
    mealCategoriesDescription: pick(data?.mealCategoriesDescription, HOMEPAGE_FALLBACKS.mealCategoriesDescription),
    ctaHeading: pick(data?.ctaHeading, HOMEPAGE_FALLBACKS.ctaHeading),
    ctaDescription: pick(data?.ctaDescription, HOMEPAGE_FALLBACKS.ctaDescription),
    ctaButtonText: pick(data?.ctaButtonText, HOMEPAGE_FALLBACKS.ctaButtonText),
    ctaButtonLink: pickLink(data?.ctaButtonLink, HOMEPAGE_FALLBACKS.ctaButtonLink),
    heroImageUrl: pickImage(data?.heroImageUrl, HOMEPAGE_FALLBACKS.heroImageUrl),
    heroImageAlt: pick(data?.heroImageAlt, HOMEPAGE_FALLBACKS.heroImageAlt),
    heroMediaType: data?.heroMediaType === "video" ? "video" : "image",
    heroVideoUrl: pickImage(data?.heroVideoUrl, HOMEPAGE_FALLBACKS.heroVideoUrl),
    heroVideoMobileUrl: pickImage(
      data?.heroVideoMobileUrl,
      HOMEPAGE_FALLBACKS.heroVideoMobileUrl,
    ),
    heroPosterUrl: pickImage(data?.heroPosterUrl, HOMEPAGE_FALLBACKS.heroPosterUrl),
    whyChooseBannerUrl: pickImage(data?.whyChooseBannerUrl, HOMEPAGE_FALLBACKS.whyChooseBannerUrl),
    founderImageUrl: pickImage(data?.founderImageUrl, HOMEPAGE_FALLBACKS.founderImageUrl),
    trustQualityImageUrl: pickImage(data?.trustQualityImageUrl, HOMEPAGE_FALLBACKS.trustQualityImageUrl),
    finalCtaBackgroundUrl: pickImage(data?.finalCtaBackgroundUrl, HOMEPAGE_FALLBACKS.finalCtaBackgroundUrl),
    howItWorksEyebrow: pick(data?.howItWorksEyebrow, HOMEPAGE_FALLBACKS.howItWorksEyebrow),
    howItWorksHeading: pick(data?.howItWorksHeading, HOMEPAGE_FALLBACKS.howItWorksHeading),
    howItWorksDescription: pick(data?.howItWorksDescription, HOMEPAGE_FALLBACKS.howItWorksDescription),
    howItWorksStep1Title: pick(data?.howItWorksStep1Title, HOMEPAGE_FALLBACKS.howItWorksStep1Title),
    howItWorksStep1Description: pick(data?.howItWorksStep1Description, HOMEPAGE_FALLBACKS.howItWorksStep1Description),
    howItWorksStep2Title: pick(data?.howItWorksStep2Title, HOMEPAGE_FALLBACKS.howItWorksStep2Title),
    howItWorksStep2Description: pick(data?.howItWorksStep2Description, HOMEPAGE_FALLBACKS.howItWorksStep2Description),
    howItWorksStep3Title: pick(data?.howItWorksStep3Title, HOMEPAGE_FALLBACKS.howItWorksStep3Title),
    howItWorksStep3Description: pick(data?.howItWorksStep3Description, HOMEPAGE_FALLBACKS.howItWorksStep3Description),
    howItWorksStep4Title: pick(data?.howItWorksStep4Title, HOMEPAGE_FALLBACKS.howItWorksStep4Title),
    howItWorksStep4Description: pick(data?.howItWorksStep4Description, HOMEPAGE_FALLBACKS.howItWorksStep4Description),
    howItWorksImageUrl: pickImage(data?.howItWorksImageUrl, HOMEPAGE_FALLBACKS.howItWorksImageUrl),
    howItWorksImageAlt: pick(data?.howItWorksImageAlt, HOMEPAGE_FALLBACKS.howItWorksImageAlt),
    isLoaded: isFetched,
  };
}